import { useEffect, useRef, useState } from 'react';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';
const SCAN_DAYS = 180;

// Results table for the 30-minute "coil" study (backend
// handlers/coil30_study.go documents the exact rule set): RSI(14)/SMA(14)
// MA above 40 rising under the RSI, MACD(12,26,9) above signal below zero
// rising, volume MA(20) rising, price flat (<= $0.05 range) in $0.10-0.20
// for a full hour -> buy the next 30-min open. All trades across the scan
// window are listed with their date - no date picker.
export default function Coil30StudyTable({ onSelect }) {
  const [data, setData] = useState(null); // Coil30Response
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const autoSelected = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const token = await getValidToken();
        if (!token || cancelled) return;
        const res = await fetch(`${API_URL}/api/coil30-study?days=${SCAN_DAYS}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        setData(json);
        if (!autoSelected.current && json.trades?.length) {
          autoSelected.current = true;
          select(json.trades[0]);
        }
        if (json.pending > 0) timer = setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setError(e.message || 'failed to load');
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (t) => {
    setSelectedKey(t.date + t.symbol + t.entryTime);
    onSelect?.({ symbol: t.symbol, date: t.date, entryTime: t.entryTime });
  };

  const trades = data?.trades || [];

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: '#e5e7eb' }}>30-min coil — trade results</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            30-min bars: RSI(14) MA(14) above 40, under RSI, rising + MACD(12,26,9) above signal, below zero,
            rising + Vol MA(20) rising + price flat (≤$0.10 range) in $0.10–$0.20 for a full hour → buy the next
            30-min open (nearest strike to spot). Max gain/loss measured to 16:00. Click a row to chart it at 30-minutes.
          </p>
        </div>
        <div className="text-right">
          {error && <span className="text-xs" style={{ color: '#ef4444' }}>Error: {error}</span>}
          {!error && data && data.pending > 0 && (
            <span className="text-xs" style={{ color: '#f59e0b' }}>
              Scanning… {data.done}/{data.sessions} sessions ({trades.length} trades so far)
            </span>
          )}
          {!error && data && data.pending === 0 && (
            <span className="text-xs text-gray-600">
              {trades.length} trades in {data.sessions} sessions · {data.from} → {data.to}
            </span>
          )}
          {!error && !data && <span className="text-xs text-gray-500">Starting scan…</span>}
        </div>
      </div>

      {!error && data && data.pending === 0 && trades.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">
          No session in the last {data.sessions} produced a signal.
        </div>
      )}

      {trades.length > 0 && (
        <div className="overflow-auto mt-3" style={{ maxHeight: 480 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Strike</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Signal</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Entry</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Max Gain</th>
                <th className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Max Loss</th>
                <th className="text-center pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">1st</th>
                <th className="text-right pb-2 text-xs font-medium text-gray-400 uppercase tracking-wider">Close 16:00</th>
              </tr>
            </thead>
            <tbody>
              {trades.map(t => {
                const isCall = t.side === 'CALL';
                const key = t.date + t.symbol + t.entryTime;
                return (
                  <tr
                    key={key}
                    onClick={() => select(t)}
                    className="border-t border-gray-800 hover:bg-gray-800/40 cursor-pointer"
                    style={selectedKey === key ? { backgroundColor: 'rgba(96,165,250,0.10)' } : undefined}
                  >
                    <td className="py-1.5 pr-3 tabular-nums text-gray-300">{t.date}</td>
                    <td className="py-1.5 pr-3">
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: isCall ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isCall ? '#22c55e' : '#ef4444',
                        }}
                      >{t.side}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: '#e5e7eb' }}>{t.strike}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">{t.sigTime}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">{t.entryTime}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: '#60a5fa' }}>${t.entryPrice.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: t.maxGainPct >= 0 ? '#22c55e' : '#6b7280' }}>
                      +{t.maxGainPct.toFixed(0)}% <span className="text-gray-600">@{t.maxGainMin}m</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums" style={{ color: '#ef4444' }}>
                      {t.maxLossPct.toFixed(0)}% <span className="text-gray-600">@{t.maxLossMin}m</span>
                    </td>
                    <td className="py-1.5 pr-3 text-center text-xs" style={{ color: t.gainFirst ? '#22c55e' : '#ef4444' }}>
                      {t.gainFirst ? 'Gain' : 'Loss'}
                    </td>
                    <td className="py-1.5 text-right tabular-nums" style={{ color: t.finalPct >= 0 ? '#22c55e' : '#ef4444' }}>
                      {t.finalPct >= 0 ? '+' : ''}{t.finalPct.toFixed(0)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
